import useApi from "@/hooks/useApi";
import React, { useCallback, useEffect, useState } from "react";
import AppLoading from "@/components/AppLoading";
import { User } from "@/pages/Messaging/Users/types";
import { UsersContext } from "@/context/UsersContext";
import { SelectOption } from "@velaro/velaro-component-lib";

export function UsersProvider(props: { children: React.ReactNode }) {
  const api = useApi();
  const [users, setUsers] = React.useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try {
      const response = await api.messaging.get("Users/List");
      const data = await response.json();
      setUsers(Array.isArray(data) ? data : []);
      return data;
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const getDisplayNameForUser = useCallback(
    (userId: number) => {
      const user = users.find((u) => u.id === userId);
      return user?.displayName || "Unknown User";
    },
    [users]
  );

  const getUserByName = useCallback(
    (name: string) => {
      return users.find(
        (user) => user.displayName.toLowerCase() === name.toLowerCase()
      );
    },
    [users]
  );

  const userOptions = users.map((user) => ({
    label: user.displayName,
    value: user.id
  })) as SelectOption[];

  if (loading) {
    return <AppLoading />;
  }

  return (
    <UsersContext.Provider
      value={{
        users,
        setUsers,
        fetchUsers: fetch,
        userOptions,
        getDisplayNameForUser,
        getUserByName
      }}
    >
      {props.children}
    </UsersContext.Provider>
  );
}
